import React, { useState, useEffect } from 'react';
import axiosInstance from '@/lib/axios';
import { 
  Shield, ShieldAlert, ShieldCheck, ShieldX, AlertTriangle, 
  Play, RefreshCw, Clock, FileText, ChevronDown, ChevronRight,
  AlertCircle, CheckCircle, Info, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const severityConfig = {
  critical: { color: 'text-red-600', bg: 'bg-red-100', icon: XCircle, label: 'Critique' },
  high: { color: 'text-orange-600', bg: 'bg-orange-100', icon: AlertTriangle, label: 'Haute' },
  medium: { color: 'text-yellow-600', bg: 'bg-yellow-100', icon: AlertCircle, label: 'Moyenne' },
  low: { color: 'text-blue-600', bg: 'bg-blue-100', icon: Info, label: 'Basse' },
  info: { color: 'text-slate-600', bg: 'bg-slate-100', icon: Info, label: 'Info' }
};

const riskLevelConfig = {
  CRITICAL: { color: 'text-red-600', bg: 'bg-red-500', label: 'Critique' },
  HIGH: { color: 'text-orange-600', bg: 'bg-orange-500', label: 'Élevé' },
  MEDIUM: { color: 'text-yellow-600', bg: 'bg-yellow-500', label: 'Moyen' },
  LOW: { color: 'text-blue-600', bg: 'bg-blue-500', label: 'Faible' },
  SECURE: { color: 'text-green-600', bg: 'bg-green-500', label: 'Sécurisé' }
};

export default function SecurityAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [runningAudit, setRunningAudit] = useState(false);
  const [latestAudit, setLatestAudit] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);
  const [expandedFindings, setExpandedFindings] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    loadAuditData();
  }, []);

  const loadAuditData = async () => {
    setLoading(true);
    try {
      // Load latest audit
      const latestRes = await axiosInstance.get(`${API}/admin/security/audit/latest`);
      if (latestRes.data && latestRes.data.scan_id) {
        setLatestAudit(latestRes.data);
      }
      
      // Load history
      const historyRes = await axiosInstance.get(`${API}/admin/security/audit/history?limit=5`);
      setAuditHistory(historyRes.data.audits || []);
      
      // Load alerts
      const alertsRes = await axiosInstance.get(`${API}/admin/security/alerts`);
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error('Error loading audit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    setRunningAudit(true);
    try {
      toast.info('Audit de sécurité en cours...');
      const response = await axiosInstance.post(`${API}/admin/security/audit/run`, {});
      setLatestAudit(response.data);
      toast.success(`Audit terminé: ${response.data.total_findings} problèmes détectés`);
      loadAuditData();
    } catch (error) {
      toast.error('Erreur lors de l\'audit');
      console.error(error);
    } finally {
      setRunningAudit(false);
    }
  };

  const toggleFinding = (id) => {
    setExpandedFindings(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getFilteredFindings = () => {
    if (!latestAudit?.findings) return [];
    if (selectedCategory === 'all') return latestAudit.findings;
    return latestAudit.findings.filter(f => f.category === selectedCategory);
  };

  const categories = latestAudit?.findings 
    ? [...new Set(latestAudit.findings.map(f => f.category))]
    : [];

  if (loading && !latestAudit) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">Audit de Sécurité</h2>
            <p className="text-sm text-slate-500">Analyse automatisée des vulnérabilités</p>
          </div>
        </div>
        
        <Button
          onClick={runAudit}
          disabled={runningAudit}
          className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
        >
          {runningAudit ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Analyse en cours...
            </>
          ) : (
            <>
              <Play className="h-4 w-4 mr-2" />
              Lancer un audit
            </>
          )}
        </Button>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
            <ShieldAlert className="h-5 w-5" />
            {alerts.length} alerte(s) de sécurité non traitée(s)
          </div>
          {alerts.slice(0, 3).map((alert, i) => (
            <div key={i} className="text-sm text-red-600 pl-7">
              • {alert.message}
            </div>
          ))}
        </div>
      )}

      {/* Latest Audit Summary */}
      {latestAudit ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Risk Score */}
            <div className="bg-white rounded-xl p-5 border border-slate-200 col-span-1">
              <div className="text-sm text-slate-500 mb-2">Score de Risque</div>
              <div className="flex items-end gap-2">
                <span className={`text-4xl font-bold ${
                  latestAudit.risk_score >= 50 ? 'text-red-600' : 
                  latestAudit.risk_score >= 25 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {latestAudit.risk_score}
                </span>
                <span className="text-slate-400 mb-1">/100</span>
              </div>
              <div className={`mt-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                riskLevelConfig[latestAudit.risk_level]?.bg
              } text-white`}>
                {riskLevelConfig[latestAudit.risk_level]?.label || latestAudit.risk_level}
              </div>
            </div>

            {/* Findings Summary */}
            <div className="bg-white rounded-xl p-5 border border-slate-200 col-span-3">
              <div className="text-sm text-slate-500 mb-3">Vulnérabilités Détectées</div>
              <div className="flex items-center gap-6">
                {Object.entries(latestAudit.summary).map(([severity, count]) => {
                  const config = severityConfig[severity];
                  return (
                    <div key={severity} className="flex items-center gap-2">
                      <div className={`w-10 h-10 ${config.bg} rounded-lg flex items-center justify-center`}>
                        <span className={`text-lg font-bold ${config.color}`}>{count}</span>
                      </div>
                      <span className="text-sm text-slate-600">{config.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Audit Info */}
          <div className="flex items-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              Dernier scan: {new Date(latestAudit.completed_at).toLocaleString('fr-FR')}
            </div>
            <div>
              Durée: {latestAudit.duration_seconds?.toFixed(2)}s
            </div>
            <div>
              Type: {latestAudit.scan_type === 'manual' ? 'Manuel' : 'Planifié'}
            </div>
          </div>

          {/* Recommendations */}
          {latestAudit.recommendations?.length > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
              <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-600" />
                Recommandations
              </h3>
              <ul className="space-y-2">
                {latestAudit.recommendations.map((rec, i) => (
                  <li key={i} className="text-sm text-slate-700">{rec}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Category Filter */}
          {categories.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-slate-500">Filtrer:</span>
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  selectedCategory === 'all' 
                    ? 'bg-slate-800 text-white' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Tous ({latestAudit.findings?.length || 0})
              </button>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedCategory === cat 
                      ? 'bg-slate-800 text-white' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {cat} ({latestAudit.findings?.filter(f => f.category === cat).length})
                </button>
              ))}
            </div>
          )}

          {/* Findings List */}
          <div className="space-y-3">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Détails des Vulnérabilités
            </h3>
            
            {getFilteredFindings().length === 0 ? (
              <div className="bg-green-50 rounded-xl p-6 text-center">
                <ShieldCheck className="h-12 w-12 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">Aucune vulnérabilité détectée</p>
                <p className="text-sm text-green-600">Votre application est sécurisée !</p>
              </div>
            ) : (
              getFilteredFindings().map((finding) => {
                const config = severityConfig[finding.severity];
                const Icon = config.icon;
                const isExpanded = expandedFindings[finding.id];
                
                return (
                  <div 
                    key={finding.id}
                    className={`bg-white rounded-xl border border-slate-200 overflow-hidden ${
                      finding.severity === 'critical' ? 'ring-2 ring-red-200' : ''
                    }`}
                  >
                    <div 
                      className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => toggleFinding(finding.id)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 ${config.bg} rounded-lg shrink-0`}>
                          <Icon className={`h-5 w-5 ${config.color}`} />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 ${config.bg} ${config.color} rounded text-xs font-medium`}>
                              {config.label}
                            </span>
                            <span className="text-xs text-slate-400">{finding.category}</span>
                            <span className="text-xs text-slate-400">{finding.id}</span>
                          </div>
                          <h4 className="font-medium text-slate-800">{finding.title}</h4>
                          <p className="text-sm text-slate-500 mt-1 line-clamp-1">{finding.description}</p>
                        </div>
                        
                        {isExpanded ? (
                          <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />
                        )}
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-slate-50">
                        <div className="space-y-3 text-sm">
                          <div>
                            <span className="font-medium text-slate-700">Description:</span>
                            <p className="text-slate-600 mt-1">{finding.description}</p>
                          </div>
                          
                          {finding.file_path && (
                            <div>
                              <span className="font-medium text-slate-700">Fichier:</span>
                              <code className="ml-2 px-2 py-1 bg-slate-200 rounded text-xs">
                                {finding.file_path}
                                {finding.line_number && `:${finding.line_number}`}
                              </code>
                            </div>
                          )}
                          
                          {finding.code_snippet && (
                            <div>
                              <span className="font-medium text-slate-700">Code:</span>
                              <pre className="mt-1 p-2 bg-slate-800 text-green-400 rounded text-xs overflow-x-auto">
                                {finding.code_snippet}
                              </pre>
                            </div>
                          )}
                          
                          {finding.recommendation && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                              <span className="font-medium text-blue-700">💡 Recommandation:</span>
                              <p className="text-blue-600 mt-1">{finding.recommendation}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        <div className="bg-slate-50 rounded-xl p-12 text-center">
          <Shield className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Aucun audit effectué</h3>
          <p className="text-slate-500 mb-4">Lancez votre premier audit de sécurité</p>
          <Button onClick={runAudit} disabled={runningAudit}>
            <Play className="h-4 w-4 mr-2" />
            Lancer un audit
          </Button>
        </div>
      )}

      {/* Audit History */}
      {auditHistory.length > 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-semibold text-slate-800 mb-4">Historique des Audits</h3>
          <div className="space-y-2">
            {auditHistory.map((audit, i) => (
              <div 
                key={audit.scan_id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  i === 0 ? 'bg-blue-50' : 'bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${riskLevelConfig[audit.risk_level]?.bg}`} />
                  <span className="text-sm font-medium">{audit.scan_id}</span>
                  <span className="text-xs text-slate-500">
                    {new Date(audit.completed_at).toLocaleString('fr-FR')}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-slate-600">{audit.total_findings} problèmes</span>
                  <span className={`font-medium ${riskLevelConfig[audit.risk_level]?.color}`}>
                    Score: {audit.risk_score}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* CRON Setup Info */}
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl p-5 border border-slate-200">
        <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
          <Clock className="h-5 w-5 text-slate-600" />
          Configuration CRON (Audit automatique)
        </h3>
        <p className="text-sm text-slate-600 mb-3">
          Configurez un job CRON pour exécuter l'audit automatiquement chaque semaine:
        </p>
        <code className="block bg-slate-800 text-green-400 p-3 rounded-lg text-sm overflow-x-auto">
          POST https://achatimmogagnant.fr/api/cron/security-audit<br/>
          Header: X-CRON-Key: [votre-clé-cron]
        </code>
      </div>
    </div>
  );
}
